import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Facebook, Instagram, DollarSign, Percent, CreditCard, Calendar, BarChart, FileText } from 'lucide-react';
import { internalHref } from '../utils/internalHref';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const footerLinks = {
    calculators: [
      { name: "Car Loan Calculator", path: "/car-loan-calculator" },
      { name: "Car Loan EMI Calculator", path: "/car-loan-emi-calculator" },
      { name: "Car Loan Interest Calculator", path: "/car-loan-interest-calculator" },
      { name: "Car Loan Amortization Calculator", path: "/car-loan-amortization-calculator" },
      { name: "Early Payoff Calculator", path: "/early-payoff-calculator" },
      { name: "Stamp Duty Calculator", path: "/stamp-duty-calculator" },
      { name: "Luxury Car Tax Calculator", path: "/luxury-car-tax-calculator" },
      { name: "Lease vs Buy Calculator", path: "/lease-vs-buy-calculator" },
      { name: "4WD Loan Calculator", path: "/4wd-loan-calculator" },
      { name: "Ute & Truck Loan Calculator", path: "/ute-truck-loan-calculator" },
      { name: "Farm Vehicle Finance Calculator", path: "/farm-vehicle-finance-calculator" },
      { name: "Military Veteran Loan Calculator", path: "/military-veteran-loan-calculator" },
      { name: "Military Relocation Loan Calculator", path: "/military-relocation-loan-calculator" },
      // Added 2026-08-19. These five shipped 2026-07-07 but were never added here,
      // so they had NO static internal inbound link anywhere on the site: the Navbar
      // renders its calculator list only inside hover/click-gated dropdowns (absent
      // from the prerendered HTML), and the Home grid omits them. GSC confirmed the
      // consequence -- balloon/refinance/payout all reported an empty referringUrls
      // set, refinance+payout linked only to each other (a closed loop with no entry
      // point), and balloon has been out of the index since 2026-07-23. The one
      // July-cohort calculator that WAS in this list (LCT) was crawled and indexed
      // within 30 minutes and is the only new URL still holding rank.
      { name: "Balloon Payment Calculator", path: "/balloon-payment-calculator" },
      { name: "Novated Lease Calculator", path: "/novated-lease-calculator" },
      { name: "Chattel Mortgage Calculator", path: "/chattel-mortgage-calculator" },
      { name: "Refinance Calculator", path: "/car-refinance-calculator" },
      { name: "Car Loan Payout Calculator", path: "/car-loan-payout-calculator" }
    ],
    company: [
      { name: "About Us", path: "/about" },
      { name: "Contact", path: "/contact" },
      { name: "Blog", path: "/blog" }
    ],
    legal: [
      { name: "Privacy Policy", path: "/privacy" },
      { name: "Terms of Service", path: "/terms" }
    ],
  };

  return (
    <footer className="bg-gray-800 text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-lg font-semibold mb-4">About Car Loan Calculator</h3>
            <p className="text-gray-300">
              Our Australian car loan payment calculator helps you estimate monthly repayments, total interest costs, and provides a comprehensive breakdown of your vehicle financing options.
            </p>
            <div className="flex items-center mt-4">
              <img 
                src="/assets/Car loan repayment calculator logo website.png" 
                alt="Car Loan Repayment Calculator Logo" 
                className="h-8 mr-2 bg-white p-1 rounded"
              />
              <span className="text-blue-300 font-semibold">carloanpaymentcalculator.com.au</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-8">
            {Object.entries(footerLinks).map(([category, links], index) => (
              <div key={index}>
                <h3 className="text-lg font-semibold mb-4">{category}</h3>
                <ul className="space-y-2 text-gray-300">
                  {links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <Link to={internalHref(link.path)} className="hover:text-blue-400">
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Car Financing Terms</h3>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-center">
                <DollarSign className="h-4 w-4 text-blue-400 mr-2" />
                <span>Principal Amount</span>
              </li>
              <li className="flex items-center">
                <Percent className="h-4 w-4 text-blue-400 mr-2" />
                <span>Interest Rate</span>
              </li>
              <li className="flex items-center">
                <CreditCard className="h-4 w-4 text-blue-400 mr-2" />
                <span>Monthly Repayments</span>
              </li>
              <li className="flex items-center">
                <Calendar className="h-4 w-4 text-blue-400 mr-2" />
                <span>Loan Term</span>
              </li>
              <li className="flex items-center">
                <BarChart className="h-4 w-4 text-blue-400 mr-2" />
                <span>Comparison Rate</span>
              </li>
              <li className="flex items-center">
                <FileText className="h-4 w-4 text-blue-400 mr-2" />
                <span>Balloon Payment</span>
              </li>
            </ul>

            <h3 className="text-lg font-semibold mb-4 mt-6">Connect With Us</h3>
            <div className="flex space-x-4 mb-6">
              <a href="#" className="hover:text-blue-400">
                <Facebook className="h-6 w-6" />
              </a>
              <a href="#" className="hover:text-blue-400">
                <Instagram className="h-6 w-6" />
              </a>
              <a href="/contact/" className="hover:text-blue-400">
                <Mail className="h-6 w-6" />
              </a>
            </div>
          </div>
        </div>
        
        <div className="mt-8 pt-8 border-t border-gray-700 text-center text-gray-300">
          <p>&copy; {currentYear} Car Loan Payment Calculator Australia. All rights reserved.</p>
          <p className="mt-2 text-sm">This calculator provides estimates only. Actual loan terms may vary.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;